/**
 * React Query hooks for managing KPI Variant data.
 * These hooks provide clean interfaces for fetching, caching, and mutating KPI Variants using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  CreateKPIVariantInput,
  CreateKPIVariantMutationVariables,
  DeleteKPIVariantInput,
  DeleteKPIVariantMutationVariables,
  DeleteKPIVariantResponse,
  GetKPIVariantQueryVariables,
  GetVariantsForRootKPIQueryVariables,
  GetVariantUsageSummaryQueryVariables,
  KPIUsageSummary,
  KPIVariant,
  UpdateKPIVariantInput,
  UpdateKPIVariantMutationVariables,
  UpdateKPIVariantResponse,
} from '@services/api/deckManagement/graphql/schema/API';
import {
  createKPIVariant,
  deleteKPIVariant,
  updateKPIVariant,
} from '@services/api/deckManagement/graphql/schema/mutations';
import {
  getKPIVariant,
  getVariantsForRootKPI,
  getVariantUsageSummary,
} from '@services/api/deckManagement/graphql/schema/queries';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  createQueryOrMutationKey,
  createStandardMutationOptions,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';

/**
 * Interface for useGetVariantsForRootKPI hook parameters
 * @param root_kpi_id - The Root KPI ID to fetch variants for
 * @param options - Additional React Query options
 */
export interface UseGetVariantsForRootKPIParams extends GetVariantsForRootKPIQueryVariables {
  options?: StandardQueryOptions;
}

/**
 * Interface for useGetKPIVariant hook parameters
 * @param root_kpi_id - The Root KPI ID the variant belongs to
 * @param kpi_variant_id - The specific KPI Variant ID to fetch
 * @param options - Additional React Query options
 */
export interface UseGetKPIVariantParams extends GetKPIVariantQueryVariables {
  options?: StandardQueryOptions;
}

/**
 * Interface for useGetVariantUsageSummary hook parameters
 * @param kpi_variant_id - The KPI Variant ID to fetch usage summary for
 * @param pageUsageFields - Optional array of PageUsageDetail fields to include in page_usage sub-query.
 *  If omitted, uses the standard getVariantUsageSummary query with all fields.
 * @param options - Additional React Query options
 */
export interface UseGetVariantUsageSummaryParams extends GetVariantUsageSummaryQueryVariables {
  pageUsageFields?: Array<'page_id' | 'page_name' | 'deckline_count' | 'kpi_variant_ids'>;
  options?: StandardQueryOptions;
}

// Query keys for KPI Variant queries related caching
export const kpiVariantQueryKeys = {
  all: createQueryOrMutationKey('kpiVariant'),
  forRootKPI: (rootKpiId: string) => createQueryOrMutationKey(...kpiVariantQueryKeys.all, 'forRootKPI', rootKpiId),
  detail: (rootKpiId: string, variantId: string) =>
    createQueryOrMutationKey(...kpiVariantQueryKeys.forRootKPI(rootKpiId), variantId),
  variantUsageSummary: (variantId: string) =>
    createQueryOrMutationKey(...kpiVariantQueryKeys.all, 'variantUsageSummary', variantId),
};

// Mutation keys for KPI Variant operations
export const kpiVariantMutationKeys = {
  all: createQueryOrMutationKey('kpiVariant', 'mutation'),
  create: () => createQueryOrMutationKey(...kpiVariantMutationKeys.all, 'create'),
  update: () => createQueryOrMutationKey(...kpiVariantMutationKeys.all, 'update'),
  delete: () => createQueryOrMutationKey(...kpiVariantMutationKeys.all, 'delete'),
};

/**
 * Hook to fetch all KPI Variants for a given Root KPI.
 */
export const useGetVariantsForRootKPI = ({
  root_kpi_id,
  options,
}: UseGetVariantsForRootKPIParams): UseQueryResult<KPIVariant[]> => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!root_kpi_id && (enabledOption ?? true);

  return useQuery({
    queryKey: kpiVariantQueryKeys.forRootKPI(root_kpi_id),
    queryFn: async (): Promise<KPIVariant[]> => {
      const response = await deckManagementClient.graphql({
        query: getVariantsForRootKPI,
        variables: { root_kpi_id },
      });

      if ('data' in response && response.data?.getVariantsForRootKPI) {
        return response.data.getVariantsForRootKPI.filter((item): item is KPIVariant => item !== null);
      }

      throw new Error('Failed to fetch KPI variants');
    },
    ...createStandardQueryOptions<KPIVariant[]>(restOptions),
    enabled: isEnabled,
  });
};

/**
 * Hook to fetch a single KPI Variant by its root KPI ID and variant ID.
 */
export const useGetKPIVariant = ({
  root_kpi_id,
  kpi_variant_id,
  options,
}: UseGetKPIVariantParams): UseQueryResult<KPIVariant | null> => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!root_kpi_id && !!kpi_variant_id && (enabledOption ?? true);

  return useQuery({
    queryKey: kpiVariantQueryKeys.detail(root_kpi_id, kpi_variant_id),
    queryFn: async (): Promise<KPIVariant | null> => {
      const response = await deckManagementClient.graphql({
        query: getKPIVariant,
        variables: { root_kpi_id, kpi_variant_id },
      });

      if ('data' in response && response.data?.getKPIVariant) {
        return response.data.getKPIVariant as KPIVariant;
      }

      throw new Error('Failed to fetch KPI variant');
    },
    ...createStandardQueryOptions<KPIVariant | null>(restOptions),
    enabled: isEnabled,
  });
};

/**
 * Hook to fetch usage summary for a KPI Variant, showing which decks and pages reference it.
 */
export const useGetVariantUsageSummary = ({
  kpi_variant_id,
  pageUsageFields,
  options,
}: UseGetVariantUsageSummaryParams): UseQueryResult<KPIUsageSummary | null> => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!kpi_variant_id && (enabledOption ?? true);

  const dynamicQuery = pageUsageFields?.length
    ? `
      query GetVariantUsageSummary($userAlias: String, $kpi_variant_id: String!) {
        getVariantUsageSummary(userAlias: $userAlias, kpi_variant_id: $kpi_variant_id) {
          root_kpi_id
          root_kpi_name
          total_deckline_count
          total_core_page_count
          total_page_count
          total_deck_count
          deck_usage {
            deck_id
            deck_name
            page_usage {
              core_page_name
              pages {
                ${pageUsageFields.join('\n              ')}
                 __typename
              }
              __typename
            }
            __typename
          }
          __typename
        }
      }
    `
    : null;

  return useQuery({
    queryKey: [
      ...kpiVariantQueryKeys.variantUsageSummary(kpi_variant_id),
      ...(pageUsageFields ? [pageUsageFields] : []),
    ],
    queryFn: async (): Promise<KPIUsageSummary | null> => {
      const response = await deckManagementClient.graphql({
        query: dynamicQuery ?? getVariantUsageSummary,
        variables: { kpi_variant_id },
      });

      if ('data' in response && response.data?.getVariantUsageSummary) {
        return response.data.getVariantUsageSummary as KPIUsageSummary;
      }

      throw new Error('Failed to fetch variant usage summary');
    },
    ...createStandardQueryOptions<KPIUsageSummary | null>(restOptions),
    enabled: isEnabled,
  });
};

/**
 * Hook to create a new KPI Variant under a Root KPI.
 * Invalidates the variants list for the parent root KPI on success.
 */
export const useCreateKPIVariant = (
  options?: StandardMutationOptions<KPIVariant, Error, CreateKPIVariantInput>,
): UseMutationResult<KPIVariant, Error, CreateKPIVariantInput> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: kpiVariantMutationKeys.create(),
    mutationFn: async (params: CreateKPIVariantInput): Promise<KPIVariant> => {
      const response = await deckManagementClient.graphql({
        query: createKPIVariant,
        variables: { request: params } as CreateKPIVariantMutationVariables,
      });

      if ('data' in response && response.data?.createKPIVariant) {
        return response.data.createKPIVariant as KPIVariant;
      }

      throw new Error('Failed to create KPI variant');
    },
    ...createStandardMutationOptions<KPIVariant, Error, CreateKPIVariantInput>({
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: kpiVariantQueryKeys.forRootKPI(variables.root_kpi_id) });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};

/**
 * Hook to update an existing KPI Variant.
 * Invalidates the variants list and detail caches on success.
 */
export const useUpdateKPIVariant = (
  options?: StandardMutationOptions<UpdateKPIVariantResponse, Error, UpdateKPIVariantInput>,
): UseMutationResult<UpdateKPIVariantResponse, Error, UpdateKPIVariantInput> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: kpiVariantMutationKeys.update(),
    mutationFn: async (params: UpdateKPIVariantInput): Promise<UpdateKPIVariantResponse> => {
      const response = await deckManagementClient.graphql({
        query: updateKPIVariant,
        variables: { request: params } as UpdateKPIVariantMutationVariables,
      });

      if ('data' in response && response.data?.updateKPIVariant) {
        return response.data.updateKPIVariant as UpdateKPIVariantResponse;
      }

      throw new Error('Failed to update KPI variant');
    },
    ...createStandardMutationOptions<UpdateKPIVariantResponse, Error, UpdateKPIVariantInput>({
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: kpiVariantQueryKeys.forRootKPI(variables.root_kpi_id) });
        queryClient.invalidateQueries({
          queryKey: kpiVariantQueryKeys.detail(variables.root_kpi_id, variables.kpi_variant_id),
        });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};

/**
 * Hook to delete a KPI Variant.
 * Invalidates the variants list for the parent root KPI on success.
 * Note: The response includes `root_kpi_auto_deleted` indicating if the root KPI was also deleted (last variant removed).
 */
export const useDeleteKPIVariant = (
  options?: StandardMutationOptions<DeleteKPIVariantResponse, Error, DeleteKPIVariantInput>,
): UseMutationResult<DeleteKPIVariantResponse, Error, DeleteKPIVariantInput> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: kpiVariantMutationKeys.delete(),
    mutationFn: async (params: DeleteKPIVariantInput): Promise<DeleteKPIVariantResponse> => {
      const response = await deckManagementClient.graphql({
        query: deleteKPIVariant,
        variables: { request: params } as DeleteKPIVariantMutationVariables,
      });

      if ('data' in response && response.data?.deleteKPIVariant) {
        return response.data.deleteKPIVariant as DeleteKPIVariantResponse;
      }

      throw new Error('Failed to delete KPI variant');
    },
    ...createStandardMutationOptions<DeleteKPIVariantResponse, Error, DeleteKPIVariantInput>({
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: kpiVariantQueryKeys.forRootKPI(variables.root_kpi_id) });
        options?.onSuccess?.(data, variables);
      },
    }),
  });
};
